import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'RevTech DenialIQ',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Dental denial management software for U.S. DSOs. Tracks denials by payer and CDT code, generates appeal letters, identifies root causes, and closes the loop with integrated payment reconciliation via ReconIQ.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'RevTech Partners',
        url: 'https://revtechpartners.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is dental denial management software?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dental denial management software automates the process of tracking, categorizing, appealing, and resolving denied insurance claims. For a dental group, this means capturing every denial from the clearinghouse or payer, categorizing it by reason code (CARC/RARC), routing it to the appropriate AR specialist, generating appeal documentation, tracking deadlines, and measuring outcomes. Enterprise dental denial management tools also provide analytics — showing denial rates by payer, CDT code, location, and provider so root causes can be addressed systematically rather than claim by claim.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is a typical denial rate for dental practices?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Industry benchmarks suggest dental practices experience denial rates between 5% and 15% of submitted claims, depending on payer mix, coding accuracy, and whether pre-authorization processes are in place. For a DSO billing several thousand claims per month, even a 2-point improvement in denial rate — from 10% to 8% — can represent significant annual revenue recovery. The greater opportunity is in overturn rate: studies suggest 50% to 70% of denied dental claims are recoverable with proper appeal workflows, but without a systematic denial management process, many are written off.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does DenialIQ integrate with our existing PMS and clearinghouse?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `DenialIQ receives denial data through the NDS DataVault integration layer, which connects directly to Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, and Xentrios. ERA files from clearinghouses are parsed automatically. Denials flow into the DenialIQ workspace within minutes of payer adjudication — no manual import required. If you're using Xentrios as your PMS, DenialIQ is natively integrated with no additional configuration.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Does DenialIQ generate appeal letters automatically?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. DenialIQ includes an AI-assisted appeal letter generator that produces payer-specific appeal correspondence based on the denial reason code, CDT code, and payer. Templates are pre-built for all major U.S. dental insurance carriers and are maintained by the RevTech clinical operations team. AR specialists can review and edit generated letters before submission, and all appeal activity is logged with outcome tracking so you know which approaches work for which payers.',
          },
        },
      ],
    },
  ],
};

const features = [
  {
    icon: '🗂️',
    name: 'Automated Denial Categorization',
    desc: 'Every incoming denial is automatically categorized by CARC code, denial type, payer, CDT code, and location. No manual triage required. Specialists open their queue and see structured, actionable work — not a raw list of remittance lines.',
  },
  {
    icon: '🔢',
    name: 'CARC/RARC Tracking',
    desc: 'Full Claim Adjustment Reason Code and Remittance Advice Remark Code tracking with payer-specific interpretations. DenialIQ maintains a CARC library that maps each code to the correct appeal strategy for U.S. dental payers.',
  },
  {
    icon: '✉️',
    name: 'Appeal Letter Generation',
    desc: 'AI-assisted appeal letter generation based on denial reason, CDT code, and payer. Pre-built templates for all major U.S. dental carriers. Specialists review and submit — the AI handles the first draft every time.',
  },
  {
    icon: '📉',
    name: 'Root Cause Analytics',
    desc: 'Aggregate denial data to identify systemic root causes: a coding pattern that Delta Dental consistently denies, a documentation gap that triggers pre-auth rejections, or a front desk workflow producing eligibility mismatches.',
  },
  {
    icon: '📊',
    name: 'Payer Trend Dashboards',
    desc: 'Track denial rates, overturn rates, average days to resolution, and appeal success rates by payer over time. Surface payer behavior changes before they become revenue problems — and use the data in contract negotiations.',
  },
  {
    icon: '💳',
    name: 'ReconIQ Integration',
    desc: 'Denials resolved through DenialIQ close the loop directly in ReconIQ. Appealed claims that result in payment are matched and reconciled automatically — no double entry, no lost payments, no reconciliation spreadsheets.',
  },
];

const workflowSteps = [
  { label: 'Claim Denied', color: '#c62828', sub: 'Payer adjudication' },
  { label: 'DenialIQ Categorizes', color: '#1976d2', sub: 'CARC/RARC + reason' },
  { label: 'Specialist Assigned', color: 'var(--xen-navy)', sub: 'Routed by type & payer' },
  { label: 'Appeal Generated', color: '#6a1b9a', sub: 'AI-drafted letter' },
  { label: 'Payer Decision', color: '#e65100', sub: 'Approved or upheld' },
  { label: 'ReconIQ Reconciles', color: '#388e3c', sub: 'Payment posted & matched' },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'Xentrios'];

export default function DentalDenialManagementPage() {
  const cmsPage = useCmsPage('xentrios', 'dental-denial-management');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Dental Denial Management Software');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const featureItems = c(cmsPage, 'features', 'items', features);
  const workflowItems = c(cmsPage, 'workflow', 'items', workflowSteps);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>Dental Denial Management Software</title>
        <meta
          name="description"
          content="RevTech DenialIQ is dental denial management software for U.S. DSOs: tracks denials by payer and CDT code, generates appeal letters, identifies root causes, and closes the loop with integrated payment reconciliation."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental Denial Management</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* The denial problem for DSOs */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Problem</p>
          <h2>The denial problem for DSOs</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Denials are the most controllable source of revenue leakage in a dental group — and most
            organizations are managing them reactively, if at all.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2.5rem',
            }}
          >
            {[
              {
                stat: '5–15%',
                label: 'Average dental claim denial rate',
                detail: 'Industry benchmarks for U.S. dental practices. DSOs with poor front-end workflows trend toward the high end.',
              },
              {
                stat: '$25–50',
                label: 'Cost per worked denial',
                detail: 'When staff time, appeal preparation, and follow-up calls are accounted for. Higher for complex CDT or pre-auth-related denials.',
              },
              {
                stat: '50–70%',
                label: 'Denied claims that are recoverable',
                detail: 'With a structured appeal workflow. Without one, most are written off as "contractual" or simply aged out of the AR.',
              },
            ].map((item) => (
              <div
                key={item.stat}
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '2rem 1.5rem',
                  textAlign: 'center',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  borderTop: '4px solid var(--xen-accent)',
                }}
              >
                <div
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 800,
                    color: 'var(--xen-navy)',
                    lineHeight: 1.1,
                    marginBottom: '0.5rem',
                  }}
                >
                  {item.stat}
                </div>
                <div style={{ fontWeight: 600, color: '#333', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                  {item.label}
                </div>
                <div style={{ color: '#666', fontSize: '0.875rem', lineHeight: 1.5 }}>{item.detail}</div>
              </div>
            ))}
          </div>
          <div className="xen-two-col" style={{ gap: '2.5rem' }}>
            <div>
              <p>
                Most DSOs track denials at a surface level — they know their denial rate exists somewhere in
                the clearinghouse portal, and they have an AR team that works denials in whatever order the
                aging report surfaces them. That's not a denial management strategy. That's a triage operation.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The root causes of denials — specific CDT codes that a payer routinely downcodes, pre-auth
                requirements that front desk staff don't know about, eligibility issues triggered by insurance
                change — never get addressed because the data isn't aggregated in a way that reveals patterns.
              </p>
            </div>
            <div>
              <p>
                DenialIQ changes the model from reactive to systematic. Every denial is categorized, every
                appeal is tracked, and every outcome is measured. Over time, the root cause dashboard shows
                your leadership exactly where denials are originating — and what process or payer changes
                would eliminate them.
              </p>
              <p style={{ marginTop: '1rem' }}>
                For a DSO billing 5,000 claims per month at a 10% denial rate, a 3-point reduction in that
                rate — achievable with consistent denial management — represents 150 fewer denials per month
                and significant incremental collections per year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DenialIQ feature grid */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">DenialIQ Features</p>
          <h2>Every tool your AR team needs to work denials systematically.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            DenialIQ is the denial management workspace inside the RevTech suite — built for specialist
            productivity, payer intelligence, and leadership visibility.
          </p>
          <div className="xen-features">
            {featureItems.map((f) => (
              <div key={f.name} className="xen-feature" style={{ textAlign: 'left' }}>
                <div className="xen-feature-icon" style={{ fontSize: '1.75rem' }}>{f.icon}</div>
                <h3 style={{ color: 'var(--xen-navy)' }}>{f.name}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow diagram */}
      <section className="xen-section xen-section-light xen-section-center">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Workflow</p>
          <h2>From denied claim to closed loop — every step tracked.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            DenialIQ manages the full denial lifecycle, from the moment a payer issues a denial through
            final payment reconciliation in ReconIQ.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '0',
              marginTop: '1rem',
            }}
          >
            {workflowItems.map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div
                  style={{
                    background: step.color,
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '1.1rem 1.25rem',
                    minWidth: '130px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.35rem', lineHeight: 1.3 }}>
                    {step.label}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>{step.sub}</div>
                </div>
                {i < arr.length - 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 0.35rem',
                      fontSize: '1.5rem',
                      color: '#aaa',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    →
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <p
            style={{
              marginTop: '2rem',
              color: '#666',
              fontSize: '0.9rem',
              maxWidth: '640px',
              margin: '2rem auto 0',
            }}
          >
            Every step is logged, timestamped, and reportable. Leadership dashboards show queue depth,
            average time to resolution, appeal overturn rates by payer, and specialist productivity —
            updated in real time.
          </p>
        </div>
      </section>

      {/* Integration badges */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Integrations</p>
          <h2>Works with your existing PMS.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            DenialIQ receives denial data through the NDS DataVault layer — connecting to every major
            dental PMS without requiring a separate integration project.
          </p>
          <div className="xen-integrations">
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See DenialIQ in action for your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Tell us your current denial rate and payer mix. We'll show you exactly how DenialIQ would
            categorize your denial volume and what your recovery opportunity looks like.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
